<script setup>
import {useCallbackStore} from "@js/store/CallbackStore.js";

import anna_img from "@img/team/anna-nemtinova-1-x-1.webp";
import denis_img from "@img/team/denis-nekotenev-1-x-1.webp";
import tigran_img from "@img/team/tigran-mirzadjayan-1-x-1.webp";

import IconTelegram from "@js/components/Icons/IconTelegram.vue";
import IconEnvelope from "@js/components/Icons/IconEnvelope.vue";
import IconWhatsApp from "@js/components/Icons/IconWhatsApp.vue";
import IconMax from "@js/components/Icons/IconMax.vue";

const callbackStore = useCallbackStore();

const managers = [
    {
        id: 1,
        name: "Анна Немтинова",
        role: "Менеджер по работе с клиентами",
        description:
            "Подберёт металлопрокат под задачу, быстро рассчитает стоимость и сопроводит заказ от заявки до отгрузки.",
        photo: anna_img,
        email: "a.nemtinova@metallolom1-prokat.ru",
        whatsapp: "https://api.whatsapp.com/send/?phone=79255491799&text&type=phone_number&app_absent=0",
        telegram: "https://t.me/mprokat1",
        max: "https://max.ru/u/f9LHodD0cOL_Bvsr9chc4MsM-Aml6U600SvNHM_JFV_EznH-nItcigW1ybo",
    },
    {
        id: 2,
        name: "Тигран Мирзаджанян",
        role: "Менеджер по корпоративным клиентам",
        description:
            "Работает с оптом и постоянными клиентами: условия, сроки, документы, поставки под объёмы.",
        photo: tigran_img,
        email: "m.tigran@metallolom1-prokat.ru",
        whatsapp: "https://api.whatsapp.com/send/?phone=79255491799&text&type=phone_number&app_absent=0",
        telegram: "https://t.me/mprokat1",
        max: "https://max.ru/u/f9LHodD0cOL_Bvsr9chc4MsM-Aml6U600SvNHM_JFV_EznH-nItcigW1ybo",
    },
    {
        id: 3,
        name: "Денис Некотенев",
        role: "Менеджер по продажам",
        description:
            "Консультирует по ассортименту и характеристикам, помогает выбрать оптимальное решение по цене и срокам.",
        photo: denis_img,
        email: "d.nekotenev@metallolom1-prokat.ru",
        whatsapp: "https://api.whatsapp.com/send/?phone=79255491799&text&type=phone_number&app_absent=0",
        telegram: "https://t.me/mprokat1",
        max: "https://max.ru/u/f9LHodD0cOL_Bvsr9chc4MsM-Aml6U600SvNHM_JFV_EznH-nItcigW1ybo",
    },
];
</script>

<template>
    <div class="bg-grayBg p-5 md:p-8 rounded-3xl mb-5">
        <div class="flex items-start justify-between gap-4 flex-wrap">
            <div>
                <h2 class="text-xl md:text-2xl font-semibold">
                    Ваши персональные менеджеры
                </h2>
                <p class="text-sm md:text-base opacity-80 mt-1">
                    Свяжитесь с менеджером — поможем с подбором, расчётом и оформлением заказа.
                </p>
            </div>

            <button
                @click.prevent="callbackStore.showBaseCallbackPopup"
                class="max-md:w-full flex justify-center items-center text-center text-white hover:text-baseColor border-2 border-baseColor bg-baseColor md:hover:bg-transparent font-bold rounded-lg text-sm px-6 py-2.5 focus:outline-none transition cursor-pointer uppercase"
            >
                Заказать звонок
            </button>
        </div>

        <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 mt-6">
            <div
                v-for="m in managers"
                :key="m.id"
                class="bg-white rounded-3xl p-4 md:p-5 shadow-sm border border-black/5 flex flex-col"
            >
                <!-- Фото + имя -->
                <div class="flex items-center gap-4">
                    <div class="w-16 h-16 rounded-2xl overflow-hidden bg-black/5 shrink-0">
                        <img
                            :src="m.photo"
                            :alt="m.name"
                            class="w-full h-full object-cover"
                            loading="lazy"
                        />
                    </div>

                    <div class="min-w-0">
                        <div class="font-bold text-base md:text-lg leading-tight truncate">
                            {{ m.name }}
                        </div>
                        <div class="text-xs md:text-sm opacity-75 mt-1 leading-snug">
                            {{ m.role }}
                        </div>
                    </div>
                </div>

                <!-- Описание -->
                <p class="text-sm opacity-90 mt-4 leading-relaxed">
                    {{ m.description }}
                </p>

                <!-- Контакты -->
                <div class="mt-auto pt-2">
                    <ul class="flex flex-row font-medium space-x-2 text-sm">
                        <!-- WhatsApp -->
                        <li
                            v-if="m.whatsapp"
                            class="w-10 h-10 bg-whatsapp rounded-lg md:hover:rounded-[50px] transition-all duration-300"
                        >
                            <a
                                :href="m.whatsapp"
                                target="_blank"
                                rel="noopener"
                                class="w-full h-full flex items-center justify-center"
                                aria-label="WhatsApp"
                                title="WhatsApp"
                            >
                                <IconWhatsApp class="w-6 h-6 text-white" />
                            </a>
                        </li>

                        <!-- Telegram -->
                        <li
                            v-if="m.telegram"
                            class="w-10 h-10 bg-[#0088CC] rounded-lg md:hover:rounded-[50px] transition-all duration-300"
                        >
                            <a
                                :href="m.telegram"
                                target="_blank"
                                rel="noopener"
                                class="w-full h-full flex items-center justify-center"
                                aria-label="Telegram"
                                title="Telegram"
                            >
                                <IconTelegram class="text-white w-5 h-5" />
                            </a>
                        </li>

                        <!-- MAX -->
                        <li
                            v-if="m.max"
                            class="w-10 h-10 bg-amber-500 rounded-lg md:hover:rounded-[50px] transition-all duration-300 max-gradient"
                        >
                            <a :href="m.max"
                                target="_blank"
                                class="w-full h-full flex items-center justify-center"
                                aria-label="Max"
                                title="Max"
                            >
                                <IconMax class="text-white w-6 h-6" />
                            </a>
                        </li>

                        <!-- Email -->
                        <li
                            v-if="m.email"
                            class="w-10 h-10 bg-amber-500 rounded-lg md:hover:rounded-[50px] transition-all duration-300"
                        >
                            <a
                                :href="`mailto:${m.email}`"
                                class="w-full h-full flex items-center justify-center"
                                aria-label="Email"
                                title="Email"
                            >
                                <IconEnvelope class="text-white w-5 h-5" />
                            </a>
                        </li>
                    </ul>
                </div>
            </div>
        </div>
    </div>
</template>
